import { useEffect, useId, useRef, useState } from 'react';
import { CalendarRange, Check } from 'lucide-react';

import { cn } from '../lib/cn';
import { DateInput } from './DateInput';
import {
  IN_PICKER,
  PRESETS,
  hasChip,
  isCustom,
  rangeLabel,
  todayISO,
  validateCustom,
  type RangeValue,
} from '../lib/range';

/**
 * The reporting period control.
 *
 * Four chips for the periods reached for without thinking, then one calendar
 * button holding everything else: the months, and an explicit window. The chips
 * were eight before, which past about four stops reading as a set of options and
 * starts reading as a list to be searched.
 *
 * The button is not merely "Custom" — when the active period is one the chips do
 * not show, it says which. Otherwise selecting Last month would leave every chip
 * unpressed and nothing on screen saying what was being looked at.
 *
 * The window commits on Apply, not on change. A date input fires onChange for
 * every intermediate value as it is typed, so live-applying would fire a request
 * for the year 0002 while somebody types 2026.
 */
export function RangePicker({
  value,
  onChange,
}: {
  value: RangeValue;
  onChange: (next: RangeValue) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  // Dismiss on an outside click or Escape, the two things a popover has to do
  // to not feel broken.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent): void => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const elsewhere = !hasChip(value);

  return (
    <div ref={container} className="relative">
      <div className="flex flex-wrap gap-1" role="group" aria-label="Reporting period">
        {PRESETS.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => {
              setOpen(false);
              onChange({ key: option.key });
            }}
            aria-pressed={value.key === option.key}
            className={cn(chipClass, value.key === option.key ? chipOn : chipOff)}
          >
            {option.label}
          </button>
        ))}

        <button
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-pressed={elsewhere}
          className={cn(chipClass, 'inline-flex items-center gap-1', elsewhere ? chipOn : chipOff)}
        >
          <CalendarRange size={12} aria-hidden />
          {elsewhere ? rangeLabel(value) : 'Other'}
        </button>
      </div>

      {open && (
        <PeriodPanel
          value={value}
          onPick={(next) => {
            onChange(next);
            setOpen(false);
          }}
          onCancel={() => setOpen(false)}
        />
      )}
    </div>
  );
}

const chipClass = 'rounded border px-2 py-1 text-xs transition-colors';
const chipOn = 'border-accent bg-accent-soft font-medium text-accent';
const chipOff = 'border-rule text-ink-muted hover:border-rule-strong hover:text-ink';

function PeriodPanel({
  value,
  onPick,
  onCancel,
}: {
  value: RangeValue;
  onPick: (next: RangeValue) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const today = todayISO();
  const fromId = useId();
  const toId = useId();

  const [from, setFrom] = useState(isCustom(value) ? value.from : today);
  const [to, setTo] = useState(isCustom(value) ? value.to : today);
  const [error, setError] = useState<string | null>(null);

  function apply(): void {
    const problem = validateCustom(from, to);
    if (problem) {
      setError(problem);
      return;
    }
    onPick({ key: 'custom', from, to });
  }

  return (
    <div
      role="dialog"
      aria-label="Choose a period"
      /* Nearly the full screen on a phone, 16rem from `sm` up.
       *
       * A native date field on iOS has an intrinsic width of around 240px at
       * 16px type and will not shrink below it — `width: 100%` and `min-width:
       * 0` are both ignored — so in a 16rem popover the two fields hung out over
       * the right edge however they were constrained. The only reliable fix is
       * to give them a container they already fit inside. */
      className="absolute right-0 z-30 mt-1.5 w-[min(21rem,calc(100vw-2rem))] rounded-lg border border-rule bg-surface p-3 shadow-float sm:w-64"
    >
      <div className="mb-3 flex gap-1">
        {IN_PICKER.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => onPick({ key: option.key })}
            aria-pressed={value.key === option.key}
            className={cn(
              'flex-1 rounded border px-2 py-1 text-2xs transition-colors',
              value.key === option.key ? chipOn : chipOff,
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <p className="qs-eyebrow mb-2 border-t border-rule pt-2">Or a range</p>

      {/* Stacked on a phone. A touch device sets its form controls at 16px, or
          iOS zooms the page in on focus, and two date fields at that size do not
          fit side by side in a 16rem popover — the year was cut off both. */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="min-w-0">
          <label htmlFor={fromId} className="qs-eyebrow mb-1 block">
            From
          </label>
          <DateInput
            id={fromId}
            value={from}
            max={to || today}
            onChange={(event) => {
              setFrom(event.target.value);
              setError(null);
            }}
            className={dateInputClass}
          />
        </div>
        <div className="min-w-0">
          <label htmlFor={toId} className="qs-eyebrow mb-1 block">
            To
          </label>
          <DateInput
            id={toId}
            value={to}
            min={from}
            onChange={(event) => {
              setTo(event.target.value);
              setError(null);
            }}
            className={dateInputClass}
          />
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-2 text-2xs text-down">
          {error}
        </p>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-rule px-2.5 py-1 text-2xs text-ink-muted hover:text-ink"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={apply}
          className="flex items-center gap-1 rounded bg-accent px-2.5 py-1 text-2xs font-medium text-ink-inverse hover:bg-accent-hover"
        >
          <Check size={11} aria-hidden />
          Apply
        </button>
      </div>
    </div>
  );
}

const dateInputClass =
  'qs-figure w-full min-w-0 rounded border border-rule bg-surface px-1.5 py-1 text-2xs outline-none focus:border-accent';
